import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button as BsButton } from "react-bootstrap";
import { ClipLoader } from "react-spinners";
import { Wrench } from "lucide-react";
import SpecificAPI from "services/SpecificApi";
import { ClassroomCard } from "views/Components/ClassroomCard";
import { MdArrowBack } from "react-icons/md";
import { OverlayTrigger, Tooltip } from "react-bootstrap";

import { AddClassroomDialog } from "views/Components/AddClassroomModal";
import localization from "services/localization";

import { useUserStore } from "context/useUserStore";
import { useDepartmentsStore } from "context/useDepartmentsStore";
import { Building2 } from "lucide-react";
import LoadingSpinner from "views/Components/LoadingSpinner";
import { useLanguageStore } from "context/useLanguageStore";

const DepartmentPage = () => {
  const { currentLang } = useLanguageStore();
  const navigate = useNavigate();

  const { appUser } = useUserStore();
  const { departments, isDepartmentsLoading, setDepartments } = useDepartmentsStore();

  const [loading, setLoading] = useState(false);
  const [showAddDepartment, setShowAddDepartment] = useState(false);
  const [showAddDepartmentModal, setShowAddDepartmentModal] = useState(false);
  const [filterDepartments, setFilterDepartments] = useState([]);
  const [departmentType, setDepartmentType] = useState("classroom");
  const [utilitiesDepartment, setUtilitiesDepartment] = useState([]);
  const [normalDepartments, setNormalDepartments] = useState([]);

  useEffect(() => {
    if (isDepartmentsLoading || !appUser?.id) return;

    const visibleDepartments = departments.filter((department) => {
      const isOwner = department.user_id === appUser.id;
      const isMember = department.members?.includes(appUser.id);

      return isOwner || isMember;
    });

    let sortedDep = visibleDepartments.sort((a, b) => a.name.localeCompare(b.name));
    const utilitiesDepartment = sortedDep.find((d) => d.type === "utilities");

    const normalDepartments = sortedDep.filter((d) => d.type !== "utilities");

    if (normalDepartments && normalDepartments.length > 0) {
      setDepartmentType(normalDepartments[0].type);
    }
    setUtilitiesDepartment(utilitiesDepartment);
    setNormalDepartments(normalDepartments);

    setFilterDepartments(sortedDep);
    // if (visibleDepartments.length === 1) {
    //   const department = visibleDepartments[0];

    //   navigate(`/prompt-categories/${moduleId}/${department.id}`);
    // }
  }, [departments, isDepartmentsLoading, appUser?.id]);

  const handleAddDepartment = async (newClassroom) => {
    try {
      setLoading(true);
      const payload = { ...newClassroom, moduleId };
      await SpecificAPI.createClassroom(payload);
      setDepartments((prev) => [...prev, newClassroom]);
      setShowAddClassroom(false);
    } catch (err) {
      console.error("Failed to add classroom", err);
    } finally {
      setLoading(false);
    }
  };

  const handleEnterDepartment = (department) => {
    const role = appUser?.role || null;
    if (role == "teacher" || role == "administrative") {
      navigate(`/services/${department.id}`);
    } else {
      navigate(`/edu-prompt/history/department/${department.id}`);
    }
  };

  if (isDepartmentsLoading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="d-flex flex-column text-center mb-5 w-100">
      <div className="p-6 h-full main-page">
        <div className="d-flex flex-column  w-100">
          <div className="d-flex align-items-center justify-content-between w-100 mb-2">
            {appUser?.school_id?.length > 1 && (
              <div className="ml-4 mr-4">
                <OverlayTrigger
                  placement="top"
                  overlay={<Tooltip id="tooltip-back">{localization.goBack || "Go Back"}</Tooltip>}
                >
                  <span>
                    <MdArrowBack
                      size={28}
                      className="icon-btn"
                      style={{ cursor: "pointer" }}
                      onClick={() => navigate("/schools")}
                    />
                  </span>
                </OverlayTrigger>
              </div>
            )}
            {/* Left side: Icon + Title + Subtitle */}
            <div className="d-flex flex-column align-items-center text-center flex-grow-1">
              <div className="d-flex align-items-center gap-3">
                <div className="title-icon-bg d-flex h-12 w-12 items-center justify-center rounded-xl shadow-md transition-colors duration-200">
                  <Building2 size={28} className="transition-colors duration-200 title-icon" />
                </div>
                <h1 className="text-3xl font-bold m-0 page-title">
                  {departmentType === "classroom"
                    ? localization.your_classrooms || "Your Classrooms"
                    : localization.your_departments || "Your Departments"}
                </h1>
              </div>
              {/* Subtitle aligned with title */}
              <p className="text-description page-subtitle" style={{ marginTop: "4px" }}>
                {departmentType === "classroom"
                  ? localization.enter_classroom_text || "Enter a classroom to interact with discussions."
                  : localization.enter_department_text || "Enter a department to interact with discussions."}
              </p>
            </div>

            {/* Add button only for teachers */}
            {showAddDepartment && (
              <BsButton variant="primary" onClick={() => setShowAddDepartmentModal(true)}>
                + {localization.addClassroom || "Add Classroom"}
              </BsButton>
            )}
          </div>
        </div>

        {/* Classrooms Grid */}
        {loading ? (
          <div className="flex justify-center items-center h-40">
            <ClipLoader size={40} color="var(--primary-icon)" />
          </div>
        ) : (
          <>
            {utilitiesDepartment && (
              <div className="mb-6 w-100 pl-6">
                <div className="flex items-center gap-2 mb-3 opacity-70">
                  <Wrench size={20} />
                  <h2 className="text-xl font-semibold uppercase">{localization.utilities || "Utilities"}</h2>
                </div>

                <div className="row g-4">
                  <div className="col-12 col-md-6 col-lg-4">
                    <ClassroomCard classroom={utilitiesDepartment} onEnter={handleEnterDepartment} isUtility />
                  </div>
                </div>
              </div>
            )}
            <div className="row g-4 p-6" id="departments">
              <div className="flex items-center gap-2 opacity-70">
                <Building2 size={20} />
                <h2 className="text-xl font-semibold uppercase">
                  {departmentType === "classroom"
                    ? localization.classrooms || "Classrooms"
                    : localization.departments || "Departments"}
                </h2>
              </div>
              {normalDepartments.length > 0 ? (
                normalDepartments.map((department) => (
                  <div key={department.id} className="col-12 col-md-6 col-lg-4">
                    <ClassroomCard classroom={department} onEnter={handleEnterDepartment} />
                  </div>
                ))
              ) : (
                <p className="text-center text-muted">
                  {localization.noClassroom || "No classrooms found for this module."}
                </p>
              )}
            </div>
          </>
        )}
      </div>
      <AddClassroomDialog
        show={showAddDepartmentModal}
        setShow={setShowAddDepartmentModal}
        onAdd={handleAddDepartment}
        loading={loading}
      />
    </div>
  );
};

export default DepartmentPage;
